import { BrandDisplayName } from 'src/utilities/brand';
import { Brands } from 'src/utilities/shared-types';

export const GROCERIES_HERO_CTA_WIDTH = '250px';

export interface GroceriesHowItWorksStepData {
  number: number;
  title: string;
  description: string;
}

export interface GroceriesFaqItemData {
  question: string;
  answer: string;
}

export interface GroceriesLegalLinks {
  grubhubTerms: string;
  grubhubPrivacy: string;
  instacartTerms: string;
  instacartPrivacy: string;
  infoSharing: string;
}

function buildGroceriesContent(brandName: string) {
  return {
    page: {
      subtitle: 'Order snacks and supplies for your office',
    },
    hero: {
      title: 'Stock the office with snacks & supplies',
      subtitle: `Groceries on ${brandName}, now powered by Instacart`,
      cta: {
        label: 'Order now',
      },
      legalLinks: {
        grubhubTerms: '/legal/terms-of-use',
        grubhubPrivacy: '/legal/privacy-policy',
        instacartTerms: 'https://www.instacart.com/terms',
        instacartPrivacy: 'https://www.instacart.com/privacy',
        infoSharing:
          'https://lp.grubhub.com/legal/instacart_information_sharing/',
      },
    },
    howItWorks: {
      title: 'How it works',
      steps: [
        {
          number: 1,
          title: 'Click "Order now"',
          description:
            "You'll go straight to Instacart—no new account or login needed.",
        },
        {
          number: 2,
          title: 'Shop your stores',
          description:
            'Browse pantry staples and snacks from grocery stores near your office, all in one place.',
        },
        {
          number: 3,
          title: 'Check out instantly',
          description: `Your ${brandName} card on file is used automatically. Grubhub+ members get $0 delivery fees on eligible orders over $35. Terms and service fees apply.`,
        },
        {
          number: 4,
          title: 'Track your delivery',
          description: `Get live order status and ETA updates on ${brandName}.com or the ${brandName} app.`,
        },
      ],
    },
    faqs: {
      title: 'FAQs',
      items: [
        {
          question: 'Do I need to create an Instacart account?',
          answer:
            'No. Clicking "Order now" takes you straight into the Instacart experience—no separate login or account setup required.',
        },
        {
          question: 'How do I pay for Instacart orders?',
          answer: `Your ${brandName} payment information on file is passed through automatically. Instacart charges that payment method directly.`,
        },
        {
          question: 'Can I use my meal credits for Instacart orders?',
          answer:
            "Not yet. Meal credits currently apply to team meal orders only. We're working on making that possible for Instacart orders in the future.",
        },
        {
          question: `Will Instacart orders show up on my ${brandName} invoice?`,
          answer:
            'Not at this time. Instacart orders are billed separately. Consolidated invoicing is in the works for the future.',
        },
        {
          question: 'Can more than one admin place Instacart orders?',
          answer:
            'Yes, any admin with access to the Business Admin portal can place Instacart orders.',
        },
        {
          question:
            'Where can I view my past Instacart orders or check order status?',
          answer: `You can check on ${brandName.toLowerCase()}.com or the ${brandName} app. Order history and tracking aren't available in the Business Admin portal yet.`,
        },
        {
          question: 'What address will my order be delivered to?',
          answer: `The office address on file with ${brandName}.`,
        },
        {
          question: 'What if I need help with a Instacart order?',
          answer:
            'Contact Instacart support directly, since those orders are placed and fulfilled through Instacart.',
        },
      ],
    },
  };
}

export type GroceriesContent = ReturnType<typeof buildGroceriesContent>;

export const GRUBHUB_GROCERIES_CONTENT: GroceriesContent =
  buildGroceriesContent(BrandDisplayName.GRUBHUB);

export const SEAMLESS_GROCERIES_CONTENT: GroceriesContent =
  buildGroceriesContent(BrandDisplayName.SEAMLESS);

export function getGroceriesContent(brand?: string): GroceriesContent {
  return brand?.toUpperCase() === Brands.SEAMLESS
    ? SEAMLESS_GROCERIES_CONTENT
    : GRUBHUB_GROCERIES_CONTENT;
}
